import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Wallpaper } from '@/types'

import Link from 'next/link'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { WallpaperPreview } from '@/components/wallpapers/wallpaper-preview'
import { WallpaperDetails } from '@/components/wallpapers/wallpaper-details'
import { WallpaperMasonry } from '@/components/wallpapers/wallpaper-masonry'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { getAllWallpaperSlugs, getRelatedWallpapers, getWallpaperBySlug } from '@/lib/wallpapers'
import { REVALIDATE_SECONDS } from '@/constants/site'

interface WallpaperPageProps {
    wallpaper: Wallpaper
    related: Array<Wallpaper>
}

const WallpaperPage: FC<WallpaperPageProps> = ({ wallpaper, related }): ReactNode => {
    const hasCategory = Boolean(wallpaper.category_id && wallpaper.category_name && wallpaper.category_slug)
    const relatedFromCategory = hasCategory && related.some(item => item.category_id === wallpaper.category_id)

    return (
        <Layout>
            <Seo
                title={`${wallpaper.title} wallpaper`}
                description={wallpaper.description || `Download ${wallpaper.title}, a free ${wallpaper.portrait ? 'phone' : 'desktop'} wallpaper.`}
                ogImage={wallpaper.image}
            />

            <section id='wallpaper' className='pt-6 sm:pt-8'>
                <Container>
                    <nav aria-label='Breadcrumb'>
                        <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                            <li>
                                <Link href='/' className='rounded transition hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'>
                                    Home
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            {hasCategory ? (
                                <li>
                                    <Link
                                        href={`/categories/${wallpaper.category_slug}`}
                                        className='rounded transition hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                                    >
                                        <span data-mdlfy-cms={`categories:${wallpaper.category_id}:name`}>
                                            {wallpaper.category_name}
                                        </span>
                                    </Link>
                                </li>
                            ) : (
                                <li>
                                    <Link href='/wallpapers' className='rounded transition hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'>
                                        Wallpapers
                                    </Link>
                                </li>
                            )}

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li aria-current='page' className='max-w-[60vw] truncate text-foreground'>
                                <span data-mdlfy-cms={`wallpapers:${wallpaper.id}:title`}>
                                    {wallpaper.title}
                                </span>
                            </li>
                        </ol>
                    </nav>

                    <div className='mt-6 grid gap-10 lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-12 xl:grid-cols-[minmax(0,1fr)_25rem]'>
                        <div id='preview' className='min-w-0'>
                            <WallpaperPreview key={wallpaper.id} wallpaper={wallpaper} />
                        </div>

                        <aside id='details' aria-label='Wallpaper details' className='lg:pt-14'>
                            <WallpaperDetails wallpaper={wallpaper} />
                        </aside>
                    </div>
                </Container>
            </section>

            {related.length > 0 && (
                <section id='related' aria-labelledby='related-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <SectionHeading
                            id='related-title'
                            eyebrow={relatedFromCategory ? 'Same mood' : 'Keep exploring'}
                            title='More like this'
                            action={hasCategory ? (
                                <Link
                                    href={`/categories/${wallpaper.category_slug}`}
                                    className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'rounded-full')}
                                >
                                    {'More in '}
                                    <span data-mdlfy-cms={`categories:${wallpaper.category_id}:name`}>
                                        {wallpaper.category_name}
                                    </span>
                                    <ArrowRight aria-hidden='true' />
                                </Link>
                            ) : undefined}
                        />

                        <WallpaperMasonry wallpapers={related} className='mt-10' />
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getAllWallpaperSlugs()

    return {
        paths: slugs.map(slug => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<WallpaperPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const wallpaper = slug ? await getWallpaperBySlug(slug) : null

    if (!wallpaper) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const related = await getRelatedWallpapers(wallpaper, 8)

    return {
        props: { wallpaper, related },
        revalidate: REVALIDATE_SECONDS
    }
}

export default WallpaperPage
